import { useState, useEffect } from 'react'
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  Divider,
  Button,
  CircularProgress,
} from '@mui/material'
import { NotificationsOutlined, NotificationsNoneOutlined } from '@mui/icons-material'
import { useNotificationStore } from '@/store/useNotificationStore'
import { useAuthStore } from '@/store/useAuthStore'
import NotificationItem from './NotificationItem'

export default function NotificationCenter() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const currentUserId = useAuthStore((state) => state.currentUserId)
  const { notifications, unreadCount, loading, fetchNotifications, markAllAsRead, clearAllNotifications } = useNotificationStore()

  useEffect(() => {
    if (currentUserId) {
      fetchNotifications(currentUserId)
      // Poll for new notifications every 30 seconds
      const interval = setInterval(() => {
        fetchNotifications(currentUserId)
      }, 30000)

      return () => clearInterval(interval)
    }
  }, [currentUserId, fetchNotifications])

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleMarkAllRead = async () => {
    if (currentUserId) {
      await markAllAsRead(currentUserId)
    }
  }

  const handleClearAll = async () => {
    if (currentUserId && window.confirm('Clear all notifications?')) {
      await clearAllNotifications(currentUserId)
      handleClose()
    }
  }

  const open = Boolean(anchorEl)

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        size="medium"
        sx={{
          borderRadius: 2,
          '&:hover': {
            bgcolor: 'rgba(255,255,255,0.15)',
            transform: 'scale(1.05)',
          },
          transition: 'all 0.2s ease-in-out',
        }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          sx={{
            '& .MuiBadge-badge': {
              animation: unreadCount > 0 ? 'pulse 2s infinite' : 'none',
              '@keyframes pulse': {
                '0%, 100%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.1)' },
              },
            },
          }}
        >
          {unreadCount > 0 ? <NotificationsOutlined /> : <NotificationsNoneOutlined />}
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 600,
            mt: 1,
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <Box
          sx={{
            p: 2,
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            color: 'white',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {unreadCount} unread
            </Typography>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <NotificationsNoneOutlined sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <>
            <List sx={{ maxHeight: 400, overflow: 'auto', p: 0 }}>
              {notifications.map((notification, index) => (
                <Box key={notification.id}>
                  <NotificationItem notification={notification} />
                  {index < notifications.length - 1 && <Divider />}
                </Box>
              ))}
            </List>

            <Divider />

            <Box sx={{ p: 1.5, display: 'flex', gap: 1 }}>
              <Button
                size="small"
                onClick={handleMarkAllRead}
                disabled={unreadCount === 0}
                fullWidth
              >
                Mark all read
              </Button>
              <Button
                size="small"
                onClick={handleClearAll}
                disabled={notifications.length === 0}
                color="error"
                fullWidth
              >
                Clear all
              </Button>
            </Box>
          </>
        )}
      </Popover>
    </>
  )
}
